import { useEffect, useRef, useState } from 'react';

import { useDesktopConnectivity } from '@/hooks/desktop/useDesktopConnectivity';
import { invalidateKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { logger } from '@/utils/logger/logger';

/**
 * The snapshot's value before any connectivity change has been observed.
 *
 * A mount is not a connectivity change, so there is no error instance it can
 * honestly hold. Seeding it with whatever the query happens to be carrying
 * would assert a change that never happened and age a pre-existing failure
 * against an episode it did not predate — marking a genuine block stale on the
 * very first frame. Nothing would then un-mark it: `staleTime` is Infinity, the
 * refetch below is edge-triggered, and on web connectivity never transitions at
 * all. A private symbol can never be an error instance, so it always compares
 * unequal and a failure present at mount reads as fresh.
 */
const NO_CONNECTIVITY_CHANGE_YET = Symbol('no connectivity change observed yet');

/**
 * `previousConnectivityRef`'s value before the first effect has run.
 *
 * Distinct from every real connectivity state so that mounting *already* online
 * and errored counts as an entry into `online` and fires the recovery refetch.
 * That is the other half of treating a mount-time error as fresh: rather than
 * guess whether the failure belongs to this episode, ask the network. If it was
 * a genuine block the refetch fails again with a new instance (still fresh, so
 * the modal shows); if it was left over, the refetch succeeds and there is no
 * error left to gate on.
 */
const BEFORE_FIRST_READING = 'before-first-reading';

interface KindePropertiesReconnectRecoveryParams {
  /** The kinde-properties query is in its error state. */
  isError: boolean;
  /**
   * The query's current error instance. Identity — not a boolean — is what
   * separates a fresh failure from the one already on screen; a retried request
   * always rejects with a new error object. `useKindePropertiesErrorToast` keys
   * off the same identity for the same reason.
   */
  error: unknown;
  /**
   * The query has a request in flight (React Query's `isFetching`).
   *
   * Identity alone cannot age an error that does not exist yet. The query
   * retries three times with a 2s/4s/8s backoff, so a request that began failing
   * in the offline window is routinely still in flight when the link returns and
   * its terminal failure lands *after* the reconnect — with no instance for the
   * edge to snapshot, it would read as a fresh failure produced inside a healthy
   * episode. A fetch already in flight when the episode began was sent over the
   * old link, so nothing it returns is evidence about this one.
   *
   * A retry chain counts as one fetch **while the retryer is actually running**,
   * which is what makes the window wide enough to matter: the error dispatch is
   * what returns `fetchStatus` to `idle`, so it reads `fetching` across the
   * attempts and the backoff gaps between them.
   *
   * `isFetching` is `fetchStatus === 'fetching'`, and `idle` is not the only
   * other value — a retryer that fails an attempt while the online manager
   * reports offline `pause()`s instead of retrying, dispatching
   * `fetchStatus: 'paused'`, which reads false here. That is unreachable on
   * macOS (WKWebView pins `navigator.onLine` true) but ordinary on Windows,
   * where the same drop that makes this hook read offline also pauses the query.
   *
   * Not arming there is CORRECT, not merely harmless, and the distinction is the
   * whole reason this is `isFetching` rather than the exhaustive
   * `fetchStatus !== 'idle'`. `pause()` happens *between* attempts, after the
   * previous one has already rejected, so a paused query has nothing
   * outstanding over the old link — and a fetch started while offline goes
   * straight to `paused` without sending at all. The next attempt goes out
   * after `continue()`, over the NEW link, so what it returns is honest evidence
   * about this episode and a failure there SHOULD raise the modal. Widening to
   * `fetchStatus !== 'idle'` would discredit a genuine block on the live link.
   * The Windows walk is input-identical to the inside-the-episode case and is
   * pinned by that test.
   *
   * This assumes the terminal failure and the end of the fetch arrive in the
   * SAME render, and they do, structurally: `query-core`'s reducer handles
   * `type: 'error'` by returning a single state object carrying `error`,
   * `status: 'error'` and `fetchStatus: 'idle'` together, and that one dispatch
   * produces one reducer call and one batched observer notify (see the
   * `case "error"` branch and the `notifyManager.batch` closing `#dispatch` in
   * `@tanstack/query-core` `build/modern/query.js`, pinned at 5.79.0). There is
   * no intermediate state where the fetch has stopped and the error has not
   * landed. Were that ever to split, this guard fails open rather than closed —
   * the settle would snapshot a null error and consume the recovery, and the
   * failure arriving one render later would read as fresh — so an upgrade that
   * touches the error dispatch is the thing to re-check here.
   */
  isFetching: boolean;
}

interface KindePropertiesReconnectRecoveryResult {
  /**
   * The kinde-properties error is not usable as a live signal that Heidi is
   * unreachable: either connectivity is not confirmed online at all, or the
   * error predates the current online episode (it is left over from a failure
   * that happened while the app was offline, or before the probe resolved).
   * Nothing may tell the user their network is broken off a stale error.
   */
  isKindePropertiesErrorStale: boolean;
}

/**
 * Keeps the non-dismissible network-error modal honest across a reconnect.
 *
 * The modal's open condition is `!hasRegion && isKindePropertiesError &&
 * hasToken && isDesktopConnectivityOnline`. Read it on a reconnect and it runs
 * backwards: going *offline* clears the connectivity term and closes the modal,
 * and coming back *online* sets it again while `isKindePropertiesError` is still
 * the failure from the offline window — so the modal opens on reconnect, over an
 * operation that has usually already succeeded. Retry appeared to work only
 * because Retry is a manual refetch that replaces the stale error.
 *
 * Two things fix that, and both are needed:
 *
 * 1. **Age the error against the online episode.** The error instance present
 *    when connectivity last changed is snapshotted, so while the query is still
 *    carrying the instance it entered `online` with, the error is *stale* and no
 *    connectivity surface may be drawn from it. A failure produced *inside* the
 *    online episode is a new instance, so a genuine firewall block still
 *    surfaces immediately. An instance cannot age a failure that has not landed
 *    yet, so a request already in flight when the episode began is discredited
 *    on the same terms — see `isFetching` on the params for why one routinely
 *    crosses that edge.
 *
 * 2. **Drive the recovery refetch.** React Query's `refetchOnReconnect` never
 *    fires here: WKWebView keeps `navigator.onLine === true` on a link that lost
 *    its route, so the library never sees the reconnect. The Rust probe is the
 *    authority, so the transition into `online` invalidates the query itself.
 *
 * That refetch fires on entry into `online` from *any* other state, not from
 * `offline`. A cold relaunch resolves `pending → online`, with the initial query
 * having already failed before the probe's first reading landed; narrowing this
 * to `offline` would leave that boot with no recovery at all.
 *
 * Web is unaffected: `useDesktopConnectivity()` is always `online` there, so the
 * episode begins at mount with no error snapshotted and every failure is fresh.
 */
export function useKindePropertiesReconnectRecovery({
  isError,
  error,
  isFetching,
}: KindePropertiesReconnectRecoveryParams): KindePropertiesReconnectRecoveryResult {
  const connectivity = useDesktopConnectivity();

  // The error instance the app was carrying when connectivity last changed —
  // i.e. the one it brought *into* the current episode. Adjusted during render
  // (not in an effect) on purpose: an effect would leave one committed render in
  // which connectivity already reads `online` and the stale error has not been
  // marked yet, which is long enough to paint the non-dismissible modal this
  // exists to suppress.
  const [connectivitySnapshot, setConnectivitySnapshot] = useState(connectivity);
  const [errorAtLastConnectivityChange, setErrorAtLastConnectivityChange] =
    useState<unknown>(NO_CONNECTIVITY_CHANGE_YET);

  // A request in flight when the current online episode began — see `isFetching`.
  const [preEpisodeFetchInFlight, setPreEpisodeFetchInFlight] = useState(false);
  // Bumped when such a request settles, so the effect below can recover from a
  // failure that arrived after the reconnect edge had passed. A counter rather
  // than a boolean: two successive settles must each be recoverable, and a flag
  // reset in an effect would drop the second.
  const [preEpisodeFetchSettleCount, setPreEpisodeFetchSettleCount] = useState(0);

  if (connectivitySnapshot !== connectivity) {
    setConnectivitySnapshot(connectivity);
    setErrorAtLastConnectivityChange(error);
    setPreEpisodeFetchInFlight(connectivity === 'online' && isFetching);
  } else if (preEpisodeFetchInFlight && !isFetching) {
    // The crossing request has settled. Discredit whatever it produced by
    // snapshotting it now, exactly as an error carried across the edge is.
    // During render for the same reason as the snapshot above: an effect leaves
    // one committed frame in which the failure reads as usable.
    setPreEpisodeFetchInFlight(false);
    setErrorAtLastConnectivityChange(error);
    setPreEpisodeFetchSettleCount((count) => count + 1);
  }

  // The reconnect refetch. Fires on entry into `online` from any non-online
  // state (`offline → online`, the cold-boot `pending → online`, and a mount
  // that is already `online`) while the query is still errored — and again when
  // a request that crossed into the episode settles into a failure, which is the
  // ordering the edge cannot see because there was no error to act on yet.
  const previousConnectivityRef = useRef<
    typeof connectivity | typeof BEFORE_FIRST_READING
  >(BEFORE_FIRST_READING);
  const handledSettleCountRef = useRef(0);
  useEffect(() => {
    const previous = previousConnectivityRef.current;
    previousConnectivityRef.current = connectivity;
    const enteredOnline = previous !== 'online' && connectivity === 'online';
    const preEpisodeFetchJustSettled =
      preEpisodeFetchSettleCount !== handledSettleCountRef.current;
    handledSettleCountRef.current = preEpisodeFetchSettleCount;
    if (preEpisodeFetchJustSettled) {
      // Reported because the discredit is otherwise an absence: over-suppression
      // looks like a clinician who never sees the firewall modal and never files
      // anything, and the retry-schedule argument for this case occurring at all
      // is reasoning that only Jest currently tests. `isError` separates a
      // crossing fetch that suppressed something from one that settled clean.
      //
      // The error itself is deliberately NOT here, and must not be added: an
      // offline-shaped Axios error stringifies the request URL, and the standing
      // rule is that identifiers reach Datadog inside library error messages
      // rather than from a line anyone wrote to log one. See
      // `.ai-context/telemetry-phi-pii-safety.md`; the payload shape is pinned
      // by a test.
      logger.info(
        '[kinde-properties-recovery] discrediting a fetch that crossed the reconnect edge',
        { isError }
      );
    }
    if (
      connectivity === 'online' &&
      isError &&
      (enteredOnline || preEpisodeFetchJustSettled)
    ) {
      void invalidateKindeProperties();
    }
  }, [connectivity, isError, preEpisodeFetchSettleCount]);

  return {
    isKindePropertiesErrorStale:
      isError &&
      // Not confirmed online: whatever failed is explained by the connection,
      // never by a block on Heidi. This is what keeps the field safe to read on
      // its own rather than only ever alongside an `isOnline` term.
      (connectivity !== 'online' || errorAtLastConnectivityChange === error),
  };
}
